import React, { useCallback, useEffect, useState } from 'react';
import {
  websiteIntegrationService,
  WebsiteIntegration as Integration,
  IssuedKey,
} from '../../services/websiteIntegrationService';
import { ListPageHeader } from '../../components/crm/ListPageHeader';

const Card: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="rounded-2xl border border-border bg-surface p-5 shadow-sm">
    <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-fg-muted">{title}</h2>
    {children}
  </div>
);

const StatusDot: React.FC<{ ok: boolean; label: string }> = ({ ok, label }) => (
  <span className="inline-flex items-center gap-2 text-sm">
    <span className={`h-2.5 w-2.5 rounded-full ${ok ? 'bg-success-fg' : 'bg-fg-muted'}`} aria-hidden />
    {label}
  </span>
);

const Toggle: React.FC<{ checked: boolean; label: string; onChange: (v: boolean) => void }> = ({ checked, label, onChange }) => (
  <label className="flex items-center justify-between py-2">
    <span className="text-sm text-fg">{label}</span>
    <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
  </label>
);

const WebsiteIntegration: React.FC = () => {
  const [data, setData] = useState<Integration | null>(null);
  const [error, setError] = useState('');
  const [originsInput, setOriginsInput] = useState('');
  const [issued, setIssued] = useState<IssuedKey | null>(null);
  const [copied, setCopied] = useState(false);
  const [testResult, setTestResult] = useState<string | null>(null);
  const [testing, setTesting] = useState(false);

  const load = useCallback(async () => {
    try {
      const d = await websiteIntegrationService.get();
      setData(d);
      setOriginsInput((d.connected_domains || []).join(', '));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const patchToggle = async (field: 'notify_new_leads' | 'notify_chatbot_leads' | 'notify_follow_ups_due', v: boolean) => {
    const updated = await websiteIntegrationService.update({ [field]: v });
    setData(updated);
  };

  const parseOrigins = () =>
    originsInput.split(',').map((s) => s.trim()).filter(Boolean);

  const issueKey = async () => {
    setError('');
    try {
      const res = await websiteIntegrationService.issueKey(parseOrigins());
      setIssued(res);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to issue key');
    }
  };

  const runTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await websiteIntegrationService.testConnection();
      setTestResult(r.ok ? '✓ Connected — a test submission was received and cleaned up.' : '✗ Test failed.');
    } catch {
      setTestResult('✗ Test failed.');
    } finally {
      setTesting(false);
    }
  };

  const snippet = issued?.embed_snippet || data?.embed_snippet || '';
  const copy = () => {
    navigator.clipboard?.writeText(snippet).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  if (!data) {
    return <div className="py-12 text-center text-fg-muted">{error || 'Loading…'}</div>;
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <ListPageHeader title="Website Integration" subtitle="Connect your website form and chatbot to capture leads." />
      {error ? <div className="alert-danger rounded-xl px-4 py-3">{error}</div> : null}

      <Card title="Connected Website">
        <div className="flex flex-wrap items-center gap-4">
          <StatusDot ok={data.lead_capture_active} label={data.lead_capture_active ? 'Lead capture active' : 'Lead capture inactive'} />
          <StatusDot ok={data.chatbot_active} label={data.chatbot_active ? 'Chatbot active' : 'Chatbot inactive'} />
        </div>
        <div className="mt-3">
          <label className="block text-xs font-medium text-fg-muted mb-1">Allowed domains (comma-separated)</label>
          <input
            value={originsInput}
            onChange={(e) => setOriginsInput(e.target.value)}
            placeholder="example.com, www.example.com"
            className="input-field w-full rounded-xl border border-border bg-input px-3 py-2 text-sm text-fg"
          />
        </div>
      </Card>

      <Card title="Lead Notifications">
        <p className="mb-2 text-xs text-fg-muted">Recipients: {data.notifications.recipients.join(', ') || '—'}</p>
        <Toggle checked={data.notifications.notify_new_leads} label="New Leads" onChange={(v) => patchToggle('notify_new_leads', v)} />
        <Toggle checked={data.notifications.notify_chatbot_leads} label="Chatbot Leads" onChange={(v) => patchToggle('notify_chatbot_leads', v)} />
        <Toggle checked={data.notifications.notify_follow_ups_due} label="Follow-Ups Due" onChange={(v) => patchToggle('notify_follow_ups_due', v)} />
      </Card>

      <Card title="Integration">
        <div className="space-y-3">
          <div className="text-sm text-fg">
            Publishable key: <code className="rounded bg-surface-muted px-1.5 py-0.5">{data.publishable_key_id || '—'}</code>
          </div>

          {issued ? (
            <div className="rounded-xl border border-warning-fg/40 bg-warning-fg/5 p-3">
              <p className="text-sm font-medium text-fg">Copy your embed code now — the key is shown only once.</p>
            </div>
          ) : null}

          <div>
            <label className="block text-xs font-medium text-fg-muted mb-1">Embed code</label>
            <div className="flex gap-2">
              <textarea
                readOnly
                value={snippet}
                rows={2}
                className="input-field w-full rounded-xl border border-border bg-input px-3 py-2 font-mono text-xs text-fg"
              />
              <button type="button" onClick={copy} disabled={!snippet} className="btn-secondary rounded-xl px-3 py-2 text-sm disabled:opacity-50">
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={issueKey} className="btn-primary rounded-xl px-4 py-2 text-sm font-medium">
              {data.publishable_key_id ? 'Rotate key & regenerate code' : 'Generate embed code'}
            </button>
            <button type="button" onClick={runTest} disabled={testing} className="btn-secondary rounded-xl px-4 py-2 text-sm font-medium disabled:opacity-50">
              {testing ? 'Testing…' : 'Test Connection'}
            </button>
          </div>
          {testResult ? <p className="text-sm text-fg">{testResult}</p> : null}
        </div>
      </Card>
    </div>
  );
};

export default WebsiteIntegration;
